import type { ListKind } from './types'
import { listGroupOf } from './listKinds.ts'
import { userKey } from './userStorage.ts'

/**
 * Quick add (Lists P3): the header's "+" opens a sheet that adds one item to
 * one list without opening the list. What is decided here is only the part
 * with a rule in it — which lists are offered, which one is already chosen
 * when the sheet opens, and what the field says — so that it can be tested
 * from the Worker's suite, which is the one `npm run check` gates on.
 * Nothing in this file touches the network or a component.
 */

/** The shape these functions read; `List` satisfies it. */
export interface QuickAddList {
  id: string
  name: string
  kind: ListKind
  pinned?: boolean | null
  archived_at?: string | null
}

/** `localStorage` key for the list last added to. A per-device convenience:
 *  losing it costs one tap, so every read and write below is guarded. */
export const LAST_LIST_KEY = 'kiez.quickadd.last'

/**
 * The lists Quick add offers, in the order the Lists index draws them:
 * pinned first, then Doing (Tasks, Checklists), Getting (Groceries,
 * Purchases), Keeping (Collections), and within each the order `useLists`
 * returned them in (`position`). Archived lists are not targets. The
 * Shortlist is not a list in this sense — it is `entry_state`, and an event
 * is saved from the feed — so it never appears here.
 */
export function quickAddTargets<L extends QuickAddList>(lists: readonly L[]): L[] {
  const GROUP = { doing: 1, getting: 2, keeping: 3 } as const
  const rank = (l: L) => (l.pinned ? 0 : GROUP[listGroupOf(l.kind)])
  return lists
    .filter(l => !l.archived_at)
    .map((l, i) => ({ l, i }))
    .sort((a, b) => rank(a.l) - rank(b.l) || a.i - b.i)
    .map(x => x.l)
}

/**
 * The list the sheet opens on.
 *
 *  1. The last list added to, if it is still a target — one that was
 *     archived or deleted since is not a reason to open on it.
 *  2. Otherwise the first Tasks list, in target order: what a bare "pay rent
 *     tomorrow" most plausibly means.
 *  3. Otherwise the first target of any kind.
 *  4. `null` when there is no target at all; the sheet then offers only
 *     "New list…".
 */
export function preselectList<L extends QuickAddList>(
  lists: readonly L[],
  lastId: string | null | undefined,
): L | null {
  const targets = quickAddTargets(lists)
  if (lastId) {
    const last = targets.find(l => l.id === lastId)
    if (last) return last
  }
  return targets.find(l => l.kind === 'task') ?? targets[0] ?? null
}

/** What the field says for the chosen list. Each kind's own screen takes a
 *  different grammar — a due date, a quantity, a link — and the placeholder is
 *  the only place the sheet can say which. */
export function placeholderFor(kind: ListKind | null, listName?: string): string {
  switch (kind) {
    case 'task': return 'Pay rent tomorrow'
    case 'grocery': return '2 kg potatoes'
    case 'buy': return 'Paste a link, or type…'
    case 'collection': return listName ? `Add to ${listName}` : 'Add…'
    case 'template': return 'Add an item'
    default: return 'Add…'
  }
}

export function readLastList(): string | null {
  try { return localStorage.getItem(userKey(LAST_LIST_KEY)) } catch { return null }
}

export function writeLastList(id: string): void {
  try { localStorage.setItem(userKey(LAST_LIST_KEY), id) } catch { /* private mode, full quota: it opens on the default */ }
}
